/**
 * TeamTrainingPage — /team-training spoke route.
 */
import { Helmet } from 'react-helmet-async';
import { Link } from '@tanstack/react-router';
import SchemaJsonLd from '../components/primitives/SchemaJsonLd.jsx';
import Eyebrow from '../components/ui/Eyebrow.jsx';
import Button from '../components/ui/Button.jsx';
import Reveal from '../components/ui/Reveal.jsx';
import {
  teamTrainingHead,
  teamTrainingIntro,
  curriculum,
  whoFits,
  format,
  teamTrainingCta,
  teamTrainingLinks,
} from '../content/teamTraining.js';

const teamTrainingServiceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Team / Enterprise Training',
  description: 'One-session AI training curriculum for teams. Claude basics, custom tool use, Claude Projects and MCPs, ethics and gotchas. Rinseable across multiple teams. 10-seat minimum per session. On-site or virtual.',
  provider: { '@type': 'Organization', name: "The Artificer's AI" },
  areaServed: { '@type': 'Country', name: 'Australia' },
  serviceType: 'Enterprise AI training',
};

const teamTrainingBreadcrumb = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://artificersai.com/' },
    { '@type': 'ListItem', position: 2, name: 'Team Training', item: 'https://artificersai.com/team-training' },
  ],
};

export default function TeamTrainingPage() {
  return (
    <>
      <Helmet>
        <title>Team AI Training — One Session, Done Properly | The Artificer's AI</title>
        <meta name="description" content="Claude basics, custom tools, MCPs, ethics. One rinseable session for your team. 10-seat minimum." />
        <link rel="canonical" href="https://artificersai.com/team-training" />
        <meta property="og:title" content="Team AI Training — One Session, Done Properly" />
        <meta property="og:description" content="Claude basics, custom tools, MCPs, ethics. One rinseable session for your team. 10-seat minimum." />
        <meta property="og:url" content="https://artificersai.com/team-training" />
      </Helmet>

      <section className="section-pad bg-surface text-ink-50">
        <div className="container-page max-w-prose">
          <Reveal as="div" delay={0} className="mb-4"><Eyebrow>{teamTrainingHead.eyebrow}</Eyebrow></Reveal>
          <Reveal as="h1" delay={80} className="text-display-lg font-display font-semibold mb-4">{teamTrainingHead.heading}</Reveal>
          <Reveal as="p" delay={140} className="text-body-lg text-ink-200 mb-8">{teamTrainingHead.subheading}</Reveal>
          <Reveal as="p" delay={200} className="text-body text-ink-200 mb-8">{teamTrainingIntro}</Reveal>
          <Reveal delay={260}><Button as={Link} to="/intake">{teamTrainingCta.label}</Button></Reveal>
        </div>
      </section>

      <section className="section-pad bg-surface-alt text-ink-50 border-t border-white/[.06]">
        <div className="container-page max-w-prose">
          <Reveal as="h2" className="text-h2 font-display font-semibold mb-6">{curriculum.heading}</Reveal>
          <ul className="flex flex-col gap-3">
            {curriculum.items.map((item, i) => (
              <Reveal as="li" key={i} delay={i * 80} className="text-body text-ink-200 pl-4 border-l border-white/[.08]">{item}</Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="section-pad bg-surface text-ink-50 border-t border-white/[.06]">
        <div className="container-page max-w-prose">
          <Reveal as="h2" className="text-h2 font-display font-semibold mb-6">{whoFits.heading}</Reveal>
          <ul className="flex flex-col gap-3">
            {whoFits.items.map((item, i) => (
              <Reveal as="li" key={i} delay={i * 80} className="text-body text-ink-200 pl-4 border-l border-white/[.08]">{item}</Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="section-pad bg-surface-alt text-ink-50 border-t border-white/[.06]">
        <div className="container-page max-w-prose">
          <Reveal as="h2" className="text-h2 font-display font-semibold mb-6">{format.heading}</Reveal>
          <ul className="flex flex-col gap-3">
            {format.items.map((item, i) => (
              <Reveal as="li" key={i} delay={i * 80} className="text-body text-ink-200 pl-4 border-l border-white/[.08]">{item}</Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="section-pad bg-surface text-ink-50 border-t border-white/[.06]">
        <div className="container-page max-w-prose">
          <div className="flex flex-col gap-4 mb-10">
            <Reveal delay={0}><Link to={teamTrainingLinks.foundry.href} className="text-body text-brand-blue-bright hover:text-brand-blue-pale transition-colors">
              {teamTrainingLinks.foundry.label}
            </Link></Reveal>
            <Reveal delay={80}><Link to={teamTrainingLinks.automatedSystems.href} className="text-body text-brand-blue-bright hover:text-brand-blue-pale transition-colors">
              {teamTrainingLinks.automatedSystems.label}
            </Link></Reveal>
            <Reveal delay={160}><Link to={teamTrainingLinks.systems.href} className="text-body text-brand-blue-bright hover:text-brand-blue-pale transition-colors">
              {teamTrainingLinks.systems.label}
            </Link></Reveal>
          </div>
          <Reveal delay={240}><Button as={Link} to="/intake">{teamTrainingCta.label}</Button></Reveal>
        </div>
      </section>

      <SchemaJsonLd data={teamTrainingServiceSchema} />
      <SchemaJsonLd data={teamTrainingBreadcrumb} />
    </>
  );
}
